import { dashboard } from '@/endpoint.flexigo';
import Repository from './repository';
import { serializeParams } from '../helpers/utility';

const client = new Repository();

export default {
  async getTaskCount(options = {}) {
    const { fromDate, toDate, campusId } = options;
    const r = await client.get(dashboard.taskCount, { fromDate, toDate, campusId });
    return r;
  },
  async getStats(options = {}) {
    const { fromDate, toDate, campusId } = options;
    const r = await client.get(dashboard.stats, { fromDate, toDate, campusId });
    return r.response;
  },
  async getRouteStats(options = {}) {
    const { campusId, fromDate, toDate, workingTypes, workgroupType } = options;

    let workgroupIds;
    if (options?.workgroupIds?.length) workgroupIds = options.workgroupIds.join(',');

    const r = await client.get(dashboard.route, {
      campusId,
      fromDate,
      toDate,
      workingTypes,
      workgroupIds,
      workgroupType,
    });
    return r;
  },
  async getTelematicDevices(options = {}) {
    const { fromDate, toDate, workgroupType } = options;
    const r = await client.get(dashboard.telematicdevices, { fromDate, toDate, workgroupType });
    return r;
  },
  async getActionsRequired() {
    const r = await client.get(dashboard.actionsRequired);
    return r;
  },
  async getCompliance(options = {}) {
    const { campusId, fromDate, routeId, toDate, workingTypes, workgroupType } = options;

    let workgroupIds;
    if (options?.workgroupIds?.length) workgroupIds = options.workgroupIds.join(',');

    let routeIds;
    if (options?.routeIds?.length) routeIds = options.routeIds.join(',');

    const r = await client.get(dashboard.compliance, {
      campusId,
      fromDate,
      routeId,
      toDate,
      workingTypes,
      workgroupIds,
      routeIds,
      workgroupType,
    });
    return r;
  },
  async getComplianceChartRidership(options = {}) {
    const { campusId, fromDate, routeId, toDate, workingTypes, workgroupType } = options;

    let workgroupIds;
    if (options?.workgroupIds?.length) workgroupIds = options.workgroupIds.join(',');

    let routeIds;
    if (options?.routeIds?.length) routeIds = options.routeIds.join(',');

    const r = await client.get(dashboard.complianceChartRidership, {
      campusId,
      fromDate,
      routeId,
      toDate,
      workingTypes,
      workgroupIds,
      routeIds,
      workgroupType,
    });
    return r;
  },
  async getComplianceNonCommute(options = {}) {
    const { campusId, fromDate, routeId, toDate, workingTypes, workgroupType } = options;

    let workgroupIds;
    if (options?.workgroupIds?.length) workgroupIds = options.workgroupIds.join(',');

    let routeIds;
    if (options?.routeIds?.length) routeIds = options.routeIds.join(',');

    const r = await client.get(dashboard.complianceNonCommute, {
      campusId,
      fromDate,
      routeId,
      toDate,
      workingTypes,
      workgroupIds,
      routeIds,
      workgroupType,
    });
    return r;
  },
  async getOccupancyRatios(options = {}) {
    const { campusId, fromDate, routeId, toDate, workingTypes, workgroupType } = options;

    let workgroupIds;
    if (options?.workgroupIds?.length) workgroupIds = options.workgroupIds.join(',');

    // `routeIds` is an array — the backend expects `collectionFormat: multi`
    // (repeated keys: routeIds=1&routeIds=2), not a comma-separated string.
    // Use serializeParams so axios emits repeated keys instead of brackets.
    const { routeIds } = options;

    const r = await client.instance.get(dashboard.occupancyRatios, {
      params: {
        campusId,
        fromDate,
        routeId,
        toDate,
        workingTypes,
        workgroupIds,
        routeIds,
        workgroupType,
      },
      paramsSerializer: serializeParams,
    });
    return r.data;
  },
  async getOccupancyRatiosAllRoutes(options = {}) {
    const { campusId, fromDate, toDate, workingTypes, workgroupType } = options;

    let workgroupIds;
    if (options?.workgroupIds?.length) workgroupIds = options.workgroupIds.join(',');

    const r = await client.get(dashboard.occupancyRatiosAllRoutes, {
      campusId,
      fromDate,
      toDate,
      workingTypes,
      workgroupIds,
      workgroupType,
    });
    return r;
  },
  async getNumberOfRiders(options = {}) {
    const { campusId, fromDate, routeId, toDate, workingTypes, workgroupType } = options;

    let workgroupIds;
    if (options?.workgroupIds?.length) workgroupIds = options.workgroupIds.join(',');

    let routeIds;
    if (Array.isArray(routeId)) {
      routeIds = routeId.join(',');
    } else {
      routeIds = routeId;
    }

    const r = await client.get(dashboard.numberOfRiders, {
      campusId,
      fromDate,
      routeIds,
      toDate,
      workingTypes,
      workgroupIds,
      workgroupType,
    });
    return r;
  },
  async getOnTimePerformance(options = {}) {
    const { campusId, fromDate, routeId, toDate, workgroupType } = options;

    let workgroupIds;
    if (options?.workgroupIds?.length) workgroupIds = options.workgroupIds.join(',');

    let routeIds;
    if (options?.routeIds?.length) routeIds = options.routeIds.join(',');

    const r = await client.get(dashboard.onTimePerformance, {
      campusId,
      fromDate,
      routeId,
      toDate,
      workgroupIds,
      routeIds,
      workgroupType,
    });
    return r;
  },
  async getOccupancy(options = {}) {
    const { fromDate, toDate, campusId } = options;
    const r = await client.get(dashboard.occupancy, { fromDate, toDate, campusId });
    return r.response;
  },
  async getRideRequests(options = {}) {
    const { campusId, fromDate, toDate, workingTypes, workgroupType } = options;

    let workgroupIds;
    if (options?.workgroupIds?.length) workgroupIds = options.workgroupIds.join(',');

    const r = await client.get(dashboard.rideRequests, {
      campusId,
      fromDate,
      toDate,
      workingTypes,
      workgroupIds,
      workgroupType,
    });
    return r;
  },
  async getTickets(options = {}) {
    const { fromDate, toDate, campusId } = options;
    const r = await client.get(dashboard.tickets, { fromDate, toDate, campusId });
    return r.response;
  },
  async getRouteStates(options = {}) {
    const { fromDate, toDate, campusId } = options;
    const r = await client.get(dashboard.routeStates, { fromDate, toDate, campusId });
    return r.response;
  },
  async getDistrictOccupancy(options = {}) {
    const { fromDate, toDate, campusId } = options;
    const r = await client.get(dashboard.districtOccupancy, { fromDate, toDate, campusId });
    return r.response;
  },

  async getDistrictData() {
    const r = await client.get(dashboard.districtMapData);
    return r.response;
  },

  async getOnDemandKpiItems(options = {}) {
    const { startDay, endDay, type } = options;
    const r = await client.get(dashboard.onDemandKpis, { startDay, endDay, type });
    return r;
  },
  async getOnDemandData(options = {}) {
    const { startDay, endDay, fromHour, toHour, type } = options;
    const r = await client.get(dashboard.onDemandData, { startDay, endDay, fromHour, toHour, type });
    return r;
  },
  async getEfficiencyData(options = {}) {
    const { startDay, endDay, fromHour, toHour, type } = options;
    const r = await client.get(dashboard.efficiencyData, { startDay, endDay, fromHour, toHour, type });
    return r;
  },
  async getOtpData(options = {}) {
    const { startDay, endDay, fromHour, toHour, type, vehicleIds, driverIds, threshold } = options;

    const r = await client.instance.get(dashboard.otpData, {
      params: { startDay, endDay, fromHour, toHour, type, vehicleIds, driverIds, threshold },
      paramsSerializer: serializeParams,
    });
    return r.data;
  },
  async getUsageData(options = {}) {
    const { startDay, endDay, fromHour, toHour, type } = options;
    const r = await client.get(dashboard.usageData, { startDay, endDay, fromHour, toHour, type });
    return r;
  },
};
